import { defineStore } from 'pinia'
import type { MenuRouteDto } from '@/models/auth'
import { normalizeMenuPath } from '@/router/catalog'

export interface NavigationItem {
  path: string
  activePath: string
  title: string
  icon?: string
  children: NavigationItem[]
}

function toNavigationItem(menu: MenuRouteDto): NavigationItem | null {
  if (menu.meta?.hidden) return null
  const path = normalizeMenuPath(menu.path)
  const children = (menu.children || []).map(toNavigationItem).filter((item): item is NavigationItem => Boolean(item))
  if (!path && !children.length) return null
  const target = children[0]?.path || path
  return {
    path: target,
    activePath: path || target,
    title: menu.name || menu.meta?.title || target,
    icon: menu.meta?.icon,
    children
  }
}

function dedupeNavigation(items: NavigationItem[]) {
  const result: NavigationItem[] = []
  const seen = new Set<string>()
  for (const item of items) {
    if (seen.has(item.activePath)) continue
    seen.add(item.activePath)
    result.push(item)
  }
  return result
}

export function buildNavigation(menus: MenuRouteDto[]) {
  const items = Array.isArray(menus)
    ? menus.map(toNavigationItem).filter((item): item is NavigationItem => Boolean(item))
    : []
  return dedupeNavigation(items)
}

export const useRouteStore = defineStore('route', {
  state: () => ({
    menus: [] as MenuRouteDto[],
    navigation: [] as NavigationItem[],
    ready: false,
    firstPath: ''
  }),
  actions: {
    setMenus(menus: MenuRouteDto[]) {
      this.menus = menus
      this.navigation = buildNavigation(menus)
      this.ready = true
      this.firstPath = this.navigation[0]?.path || ''
    },
    hydrate(menus: MenuRouteDto[]) {
      this.setMenus(Array.isArray(menus) ? menus : [])
    },
    clear() {
      this.menus = []
      this.navigation = []
      this.ready = false
      this.firstPath = ''
    }
  }
})
